// src/services/companySettings.service.js
import { api } from './api';

class CompanySettingsService {
    /**
     * Get company record directly from companies table context
     */
    async getCompanyFromCompaniesTable() {
        try {
            const response = await api.get('/api/companies/me');
            return response.data?.data || response.data?.company || response.data || {};
        } catch (error) {
            console.error('Error fetching company from companies table:', error);
            return {};
        }
    }

    /**
     * Get company profile (from companies table - includes state for PT calculation)
     */
    async getCompanyProfile() {
        try {
            const response = await api.get('/api/company/profile');
            return response.data?.data || response.data?.company || response.data || {};
        } catch (error) {
            console.error('Error fetching company profile:', error);
            return {};
        }
    }

    /**
     * Get all settings
     */
    async getSettings() {
        try {
            const response = await api.get('/api/company/settings');
            return response.data.data || {};
        } catch (error) {
            console.error('Error fetching company settings:', error);
            return this.getLocalStorageSettings();
        }
    }

    /**
     * Get specific setting by key
     */
    async getSetting(key) {
        try {
            const response = await api.get(`/api/company/settings/${key}`);
            return response.data.data || null;
        } catch (error) {
            console.error(`Error fetching setting ${key}:`, error);
            return null;
        }
    }

    /**
     * Update specific setting
     */
    async updateSetting(key, value) {
        try {
            const response = await api.put(`/api/company/settings/${key}`, { value });
            return response.data;
        } catch (error) {
            console.error(`Error updating setting ${key}:`, error);
            throw error;
        }
    }

    /**
     * Update multiple settings
     */
    async updateSettings(settings) {
        try {
            const response = await api.put('/api/company/settings', { settings });
            return response.data;
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    }

    /**
     * Delete setting
     */
    async deleteSetting(key) {
        try {
            const response = await api.delete(`/api/company/settings/${key}`);
            return response.data;
        } catch (error) {
            console.error(`Error deleting setting ${key}:`, error);
            throw error;
        }
    }

    /**
     * Get shifts
     */
    async getShifts() {
        try {
            const response = await api.get('/api/company/shifts');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching shifts:', error);
            return [];
        }
    }

    /**
     * Create shift
     */
    async createShift(shiftData) {
        try {
            const response = await api.post('/api/company/shifts', shiftData);
            return response.data;
        } catch (error) {
            console.error('Error creating shift:', error);
            throw error;
        }
    }

    /**
     * Update shift
     */
    async updateShift(id, shiftData) {
        try {
            const response = await api.put(`/api/company/shifts/${id}`, shiftData);
            return response.data;
        } catch (error) {
            console.error('Error updating shift:', error);
            throw error;
        }
    }

    /**
     * Delete shift
     */
    async deleteShift(id) {
        try {
            const response = await api.delete(`/api/company/shifts/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting shift:', error);
            throw error;
        }
    }

    /**
     * Get holiday plans
     */
    async getHolidayPlans() {
        try {
            const response = await api.get('/api/company/holiday-plans');
            return response.data.data || [];
        } catch (error) {
            console.error('Error fetching holiday plans:', error);
            return [];
        }
    }

    /**
     * Create holiday plan
     */
    async createHolidayPlan(planData) {
        try {
            const response = await api.post('/api/company/holiday-plans', planData);
            return response.data;
        } catch (error) {
            console.error('Error creating holiday plan:', error);
            throw error;
        }
    }

    /**
     * Fallback: Get settings from localStorage
     */
    getLocalStorageSettings() {
        // try {
        //     const stored = localStorage.getItem('companySettings');
        //     return stored ? JSON.parse(stored) : {};
        // } catch (error) {
        //     console.error('Error loading settings from localStorage:', error);
        //     return {};
        // }
    }

    /**
     * Save settings to localStorage
     * Handles QuotaExceededError by saving settings without logo if needed
     */
    saveSettingsToLocalStorage(settings) {
        try {
            localStorage.setItem('companySettings', JSON.stringify(settings));
        } catch (error) {
            if (error.name === 'QuotaExceededError') {
                console.warn('localStorage quota exceeded, saving settings without logo...');
                // Save without logo to avoid quota issues
                const { logo, ...settingsWithoutLogo } = settings;
                try {
                    localStorage.setItem('companySettings', JSON.stringify(settingsWithoutLogo));
                    console.log('Settings saved without logo successfully');
                    // Try to save logo separately (may fail if still too large)
                    if (logo) {
                        try {
                            localStorage.setItem('companyLogo', logo);
                        } catch (logoError) {
                            console.warn('Logo too large for localStorage, skipping logo storage');
                        }
                    }
                } catch (retryError) {
                    console.error('Error saving settings even without logo:', retryError);
                }
            } else {
                console.error('Error saving settings to localStorage:', error);
            }
        }
    }
}

export default new CompanySettingsService();
